import React from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '../ui/Button'
import { useAuth } from '../AuthProvider'
import { useI18n } from '../../i18n'
import { getActiveOrgId } from '../../utils/orgUtils'
import { isScannableAssetType } from '../../constants/assetTaxonomy'
import { isLinkableAssetId } from '../../services/informationSystemsApi'
import type { AssetOperationalDetail } from '../../services/assetOperationalApi'
import {
  type AssetPanelContext,
  ASSET_CONTEXT_NAV_ITEMS,
  assetContextNavLabel,
  assetPanelContextTitle,
  switchAssetContextKind,
  assetDrillDownToContext,
} from './assetPanelContext'
import { assetPanelContextToDrillTarget } from './assetDrillDown'
import { DrillDownContent, useDrillDownData } from './operationalDrillDownContent'
import {
  TopVulnerabilitiesList,
  TopFailedControlsList,
  TopOpenRisksList,
} from './systemPostureRowParts'
import AssetTypeOverviewSection from './AssetTypeOverviewSection'
import AssetGraphView from './AssetGraphView'
import AssetTimelineSection from './AssetTimelineSection'
import AssetScansSection from './AssetScansSection'
import AssetRelationshipsSection from './AssetRelationshipsSection'
import styles from '../../styles/components/assets/AssetsInformationSystems.module.scss'

interface Props {
  detail: AssetOperationalDetail
  summaryRow?: import('../../services/assetOperationalApi').AssetOperationalSummaryRow | null
  panelContext: AssetPanelContext
  onContextChange: (ctx: AssetPanelContext) => void
  onClose: () => void
  onRefresh: () => Promise<void>
  onEdit?: () => void
  onScan?: () => void
  onOpenSystem?: (systemId: string) => void
  informationSystems?: import('../../services/informationSystemsApi').InformationSystemSummary[]
  onLinkToSystem?: (systemId: string) => Promise<void>
  onUnlinkFromSystem?: (systemId: string) => Promise<void>
  linkingToSystem?: boolean
}

export default function AssetContextPanel({
  detail,
  summaryRow,
  panelContext,
  onContextChange,
  onClose,
  onRefresh,
  onEdit,
  onScan,
  onOpenSystem,
  informationSystems,
  onLinkToSystem,
  onUnlinkFromSystem,
  linkingToSystem,
}: Props) {
  const { t } = useI18n()
  const { token } = useAuth()
  const orgId = getActiveOrgId()
  const navigate = useNavigate()
  const primarySystemId = detail.linkedSystems?.[0]?.id
  const drillTarget = assetPanelContextToDrillTarget(panelContext, primarySystemId)
  const { loading, error, data } = useDrillDownData(drillTarget, token, orgId, t)
  const scannable = isScannableAssetType(detail.type)

  const handleDrillDown = (target: import('./operationalDrillDownContent').DrillDownTarget) => {
    if (!target) return
    onContextChange(
      assetDrillDownToContext({
        kind: target.kind,
        assetId: detail.assetId,
        id: target.kind === 'risk' ? target.sourceId : target.id,
        label: target.label,
        sourceType: target.kind === 'risk' ? target.sourceType : undefined,
        sourceId: target.kind === 'risk' ? target.sourceId : undefined,
      })
    )
  }

  const navItems = ASSET_CONTEXT_NAV_ITEMS

  function renderBody() {
    if (panelContext.kind === 'fullPassport') {
      navigate(`/assets/${detail.assetId}`)
      return <p className="text-sm text-gray-500">{t('assets.assetPassport.redirecting', 'Opening full asset page…')}</p>
    }

    if (panelContext.kind === 'overview') {
      return (
        <div className={styles.contextSectionBody}>
          <AssetTypeOverviewSection detail={detail} />
          {isLinkableAssetId(detail.assetId) && onLinkToSystem ? (
            <div className={styles.contextOverviewActions}>
              <button
                type="button"
                className={styles.contextLinkBtn}
                onClick={() => onContextChange({ kind: 'relationships', assetId: detail.assetId })}
              >
                {t('assets.assetPassport.manageSystemLinks', 'Link to information system')}
                {detail.linkedSystemsCount > 0 ? ` (${detail.linkedSystemsCount})` : ''}
              </button>
            </div>
          ) : null}
        </div>
      )
    }

    if (panelContext.kind === 'relationships') {
      return (
        <AssetRelationshipsSection
          detail={detail}
          onOpenSystem={onOpenSystem}
          informationSystems={informationSystems}
          onLinkToSystem={onLinkToSystem}
          onUnlinkFromSystem={onUnlinkFromSystem}
          linking={linkingToSystem}
        />
      )
    }

    if (panelContext.kind === 'graph') {
      return <AssetGraphView assetId={detail.assetId} />
    }

    if (panelContext.kind === 'timeline') {
      return <AssetTimelineSection assetId={detail.assetId} />
    }

    if (panelContext.kind === 'scans') {
      return <AssetScansSection assetId={detail.assetId} />
    }

    if (panelContext.kind === 'vulnerability') {
      if (drillTarget) {
        return (
          <div className={styles.contextSectionBody}>
            {loading ? <p className={styles.loadingText}>{t('common.loading')}</p> : null}
            {error ? <p className={styles.contextError}>{error}</p> : null}
            {!loading && !error && data ? (
              <DrillDownContent
                kind="vulnerability"
                data={data}
                t={t}
                target={drillTarget}
                systemId={primarySystemId || String(detail.assetId)}
                token={token}
                orgId={orgId}
                onOpenGovernance={() => window.open('/governance/risks', '_blank')}
              />
            ) : null}
          </div>
        )
      }
      return (
        <div className={styles.contextSectionBody}>
          <TopVulnerabilitiesList
            items={(summaryRow?.topVulnerabilities || detail.topVulnerabilities || []).slice(0, 10)}
            empty={t('assets.postureRow.noVulnerabilities', 'No vulnerabilities')}
            onDrillDown={handleDrillDown}
            systemId={String(detail.assetId)}
          />
        </div>
      )
    }

    if (panelContext.kind === 'control') {
      if (drillTarget) {
        return (
          <div className={styles.contextSectionBody}>
            {loading ? <p className={styles.loadingText}>{t('common.loading')}</p> : null}
            {error ? <p className={styles.contextError}>{error}</p> : null}
            {!loading && !error && data ? (
              <DrillDownContent kind="control" data={data} t={t} target={drillTarget} systemId={primarySystemId || ''} token={token} orgId={orgId} />
            ) : null}
          </div>
        )
      }
      return (
        <div className={styles.contextSectionBody}>
          <TopFailedControlsList
            items={(summaryRow?.topFailedControls || detail.topFailedControls || []).slice(0, 10)}
            empty={t('assets.postureRow.noFailedControls', 'No failed controls')}
            onDrillDown={handleDrillDown}
            systemId={String(detail.assetId)}
          />
        </div>
      )
    }

    if (panelContext.kind === 'risk') {
      if (drillTarget) {
        return (
          <div className={styles.contextSectionBody}>
            {loading ? <p className={styles.loadingText}>{t('common.loading')}</p> : null}
            {error ? <p className={styles.contextError}>{error}</p> : null}
            {!loading && !error && data ? (
              <DrillDownContent kind="risk" data={data} t={t} target={drillTarget} systemId={primarySystemId || ''} token={token} orgId={orgId} />
            ) : null}
          </div>
        )
      }
      return (
        <div className={styles.contextSectionBody}>
          <TopOpenRisksList
            items={(summaryRow?.topOpenRisks || detail.topOpenRisks || []).slice(0, 10)}
            empty={t('assets.postureRow.noOpenRisks', 'No open risks')}
            onDrillDown={handleDrillDown}
            systemId={String(detail.assetId)}
          />
        </div>
      )
    }

    if (panelContext.kind === 'evidence') {
      return (
        <p className="text-sm text-gray-600">
          {t('assets.assetPassport.evidenceHint', 'Evidence is managed per linked information system.')}{' '}
          ({detail.linkedSystemsCount} {t('assets.assetPassport.linkedSystems', 'linked systems')},{' '}
          {detail.evidenceGapCount ?? 0} {t('assets.assetPassport.evidenceGaps', 'gaps')})
        </p>
      )
    }

    if (panelContext.kind === 'remediation') {
      return (
        <p className="text-sm text-gray-600">
          {t('assets.assetPassport.remediationHint', 'Remediation tracked via linked systems.')}{' '}
          {t('assets.assetPassport.remediationOpen', 'Open')}: {detail.remediationOpenCount ?? 0}.{' '}
          {t('assets.assetPassport.remediationAging', 'Aging')}: {detail.remediationAgingCount ?? 0}.
        </p>
      )
    }

    return null
  }

  const body = renderBody()

  return (
    <div className={styles.detailsPanel}>
      <header className={styles.detailsPanelHeader}>
        <div className={styles.detailsPanelHeaderMain}>
          <p className={styles.detailsPanelLabel}>{assetPanelContextTitle(panelContext, t)}</p>
          <h3 className={styles.detailsPanelTitle} title={detail.name}>
            {detail.name}
          </h3>
          <p className={styles.detailsPanelCode}>{detail.type}</p>
        </div>
        <Button type="button" size="small" color="outline" onClick={onClose}>
          {t('assets.postureRow.closeDetails', 'Close')}
        </Button>
      </header>

      {panelContext.kind !== 'fullPassport' ? (
        <nav className={styles.contextNav} aria-label={t('assets.assetPassport.nav', 'Asset context')}>
          {navItems.map((kind) => (
            <button
              key={kind}
              type="button"
              className={`${styles.contextNavChip} ${panelContext.kind === kind ? styles.contextNavChipActive : ''}`.trim()}
              onClick={() => onContextChange(switchAssetContextKind(panelContext, kind))}
            >
              {assetContextNavLabel(kind, t)}
            </button>
          ))}
        </nav>
      ) : null}

      <div className={styles.detailsPanelBody}>{body}</div>

      <div className={styles.contextPanelFooter}>
        <button
          type="button"
          className={styles.contextLinkBtn}
          onClick={() => onContextChange({ kind: 'fullPassport', assetId: detail.assetId })}
        >
          {t('assets.assetPassport.openFullPassport', 'Open full asset passport')}
        </button>
        {scannable && onScan ? (
          <button type="button" className={styles.contextLinkBtn} onClick={onScan}>
            {t('assets.inventory.scan', 'Scan')}
          </button>
        ) : null}
        {onEdit ? (
          <button type="button" className={styles.contextLinkBtn} onClick={onEdit}>
            {t('common.edit', 'Edit')}
          </button>
        ) : null}
      </div>
    </div>
  )
}
